import { useContext, useCallback, useMemo, useState, useEffect } from 'react';
import { useSelector } from '@xstate/react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import {
  isCanceling as isStateMachineCanceling,
  StepEvents,
  isSaving as isStateMachineSaving,
} from '@bingads-webui-campaign-react/step-wizard-machine';
import { legacyI18nOfListingGroup } from '@bingads-webui-campaign-react/listing-groups-page';
import { useExitWithoutSavingConfirmation } from '@bingads-webui-campaign-react/step-wizard';
import { useImpulse, useIsMounted, usePrevProp } from '@bingads-webui-campaign-react/step-wizard-controls';
import { RecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { needsSaving } from './step-campaign-asset-group-impl';
import { ScenarioName } from '../../common/constants';
import { serverErrorsCompare } from '../shared';

const hideStepHeaderSelector = state => state.context.stepInfo.hideStepHeader;
const scenarioNameSelector = state => state.context.stepsData.scenarioName;
const permissionsSelector = state => state.context.stepsData.permissions;
const doesNeedSavingSelector = state => !isStateMachineCanceling(state) && needsSaving(state.context);
const currentLocaleSelector = state => state.context.stepsData.serverContext.CurrentLocale;
const campaignObjectiveSelector = state => state.context.stepsData.campaignObjective;
const campaignIdSelector = state => state.context.stepsData.campaign.Id;
const campaignNameSelector = state => state.context.stepsData.campaign.Name;
const campaignTimeZoneSelector = state => state.context.stepsData.campaign.TimeZone;
const campaignSettingsSelector = state => state.context.stepsData.campaign.CampaignSettings;
const assetGroupSelector = state => state.context.data.assetGroup;
const headlinesSelector = state => state.context.data.assetGroup.data.Headlines;
const longHeadlinesSelector = state => state.context.data.assetGroup.data.LongHeadlines;
const descriptionsSelector = state => state.context.data.assetGroup.data.Descriptions;
const path1Selector = state => state.context.data.assetGroup.data.Path1;
const path2Selector = state => state.context.data.assetGroup.data.Path2;
const finalMobileUrlsSelector = state => state.context.data.assetGroup.data.MobileFinalUrls;
const imagesSelector = state => state.context.data.assetGroup.data.Images;
const businessNameSelector = state => state.context.data.assetGroup.data.BusinessName;
const finalUrlsSelector = state => state.context.data.assetGroup.data.FinalUrls;
const finalUrlForRecommendationSelector = state => state.context.data.assetGroup.data.FinalUrlForRecommendation;
const adExtensionsDynamicFeaturesSelector = state => state.context.stepsData.adExtensionsDynamicFeatures;
const adExtensionsAssociationsFactorySelector = state => state.context.stepsData.adExtensionsAssociationsFactory;
const extensionsAssociationsSelector = state => state.context.data.extensionsAssociations;
const assetGroupURLRulesSelector = state => state.context.data.assetGroup.data.AssetGroupUrlTargets;
const assetGroupURLRulesOptionSelector = state => state.context.data.assetGroup.data.UrlRulesOption;
const audienceGroupSelector = state => state.context.data.assetGroup.data.AudienceGroup;
const originalAudienceGroupSelector = state => state.context.data.originalAssetGroup.data.AudienceGroup;
const listingGroupSelector = state => state.context.data.assetGroup.data.listingGroup;
const productFilterSelector = state => state.context.data.assetGroup.data.productFilter;
const searchThemesSelector = state => state.context.data.assetGroup.data.AssetGroupSearchThemes;
const aidSelector = state => state.context.stepsData.aid;
const cidSelector = state => state.context.stepsData.cid;
const serverErrorsSelector = state => state.context.data.serverErrors;
const systemErrorsSelector = state => state.context.data.systemErrors;
const invalidSaveAttemptSelector = state => state.context.invalidSaveAttempt;
const isEditingSelector = state => state.context.stepsData.isEditing;
const isSavingSelector = state => isStateMachineSaving(state);
const isCancelingSelector = state => isStateMachineCanceling(state);

export const useStepAssetGroupState = (stepService, activity) => {
  const isMounted = useIsMounted();
  const i18n = useContext(I18nContext);
  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap, legacyI18nOfListingGroup]), [i18n]);

  const hideStepHeader = useSelector(stepService, hideStepHeaderSelector);
  const scenarioName = useSelector(stepService, scenarioNameSelector);
  const locale = useSelector(stepService, currentLocaleSelector);
  const assetGroup = useSelector(stepService, assetGroupSelector);
  const finalUrls = useSelector(stepService, finalUrlsSelector);
  const finalUrlForRecommendation = useSelector(stepService, finalUrlForRecommendationSelector);
  const headlines = useSelector(stepService, headlinesSelector);
  const longHeadlines = useSelector(stepService, longHeadlinesSelector);
  const descriptions = useSelector(stepService, descriptionsSelector);
  const campaignId = useSelector(stepService, campaignIdSelector);
  const campaignName = useSelector(stepService, campaignNameSelector);
  const campaignTimeZone = useSelector(stepService, campaignTimeZoneSelector);
  const campaignObjective = useSelector(stepService, campaignObjectiveSelector);
  const campaignSettings = useSelector(stepService, campaignSettingsSelector);
  const permissions = useSelector(stepService, permissionsSelector);
  const adExtensionsDynamicFeatures = useSelector(stepService, adExtensionsDynamicFeaturesSelector);
  const adExtensionsAssociationsFactory = useSelector(stepService, adExtensionsAssociationsFactorySelector);
  const extensionsAssociations = useSelector(stepService, extensionsAssociationsSelector);
  const urlPath1 = useSelector(stepService, path1Selector);
  const urlPath2 = useSelector(stepService, path2Selector);
  const finalMobileUrls = useSelector(stepService, finalMobileUrlsSelector);
  const urlRules = useSelector(stepService, assetGroupURLRulesSelector);
  const urlRulesOption = useSelector(stepService, assetGroupURLRulesOptionSelector);
  const images = useSelector(stepService, imagesSelector);
  const businessName = useSelector(stepService, businessNameSelector);
  const audienceGroup = useSelector(stepService, audienceGroupSelector);
  const originalAudienceGroup = useSelector(stepService, originalAudienceGroupSelector);
  const listingGroup = useSelector(stepService, listingGroupSelector);
  const searchThemes = useSelector(stepService, searchThemesSelector);
  const productFilter = useSelector(stepService, productFilterSelector);
  const aid = useSelector(stepService, aidSelector);
  const cid = useSelector(stepService, cidSelector);
  const serverErrors = useSelector(stepService, serverErrorsSelector);
  const systemErrors = useSelector(stepService, systemErrorsSelector);
  const invalidSaveAttempt = useSelector(stepService, invalidSaveAttemptSelector);
  const isEditing = useSelector(stepService, isEditingSelector);
  const isSaving = useSelector(stepService, isSavingSelector);
  const isCanceling = useSelector(stepService, isCancelingSelector);

  const doesNeedSaving = useSelector(stepService, doesNeedSavingSelector);

  const { suggestedObjectiveExtensions = [], otherObjectiveExtensions = [] } = useMemo(
    () => (scenarioName === ScenarioName.PerformanceMaxCampaignCreation ?
      adExtensionsAssociationsFactory.getObjectiveExtensions(
        permissions,
        { campaignType: 'PerformanceMax', campaignObjective }
      ) : {}),
    [scenarioName, permissions, campaignObjective, adExtensionsAssociationsFactory]
  );

  const [navigationConfirmationPrompt] = useExitWithoutSavingConfirmation(doesNeedSaving, stepService, i18n);

  const onAssetGroupValidation = useCallback((results) => {
    if (!isMounted.current) return;
    const { data: newAssetGroupData, ...restResults } = results;
    const dataPatch = {
      assetGroup: {
        ...assetGroup,
        data: {
          ...assetGroup.data,
          ...newAssetGroupData,
        },
        ...restResults,
      },
      activity,
    };
    stepService.send({ type: StepEvents.UPDATE_VALIDATION, data: dataPatch });
  }, [isMounted, assetGroup, activity, stepService]);

  const onUpdateExtensionsAssociations = useCallback((extensionType, associations) => {
    if (!isMounted.current) return;
    const dataPatch = {
      extensionsAssociations: {
        ...extensionsAssociations,
        [extensionType]: associations,
      },
    };
    stepService.send({ type: StepEvents.UPDATE_VALIDATION, data: dataPatch });
  }, [stepService, extensionsAssociations, isMounted]);

  // Connecting UI layer's RecommendationContext to the step's state
  const onUpdateRecommendationsContext = useCallback((recommendationContext) => {
    if (!isMounted.current) return;
    const dataPatch = {
      recommendationContext,
    };
    stepService.send({ type: StepEvents.UPDATE_VALIDATION, data: dataPatch });
  }, [stepService, isMounted]);
  const [recommendationContext] = useContext(RecommendationContext);
  useEffect(() => {
    onUpdateRecommendationsContext(recommendationContext);
  }, [recommendationContext, onUpdateRecommendationsContext]);

  const onUpdateFinalUrls = useCallback((newFinalUrls) => {
    if (!isMounted.current) return;
    const dataPatch = {
      assetGroup: {
        ...assetGroup,
        data: {
          ...assetGroup.data,
          FinalUrls: newFinalUrls,
        },
      },
    };
    stepService.send({ type: StepEvents.UPDATE_VALIDATION, data: dataPatch });
  }, [stepService, assetGroup, isMounted]);

  const onUpdateSearchThemes = useCallback((newSearchThemes) => {
    if (!isMounted.current) return;
    const dataPatch = {
      assetGroup: {
        ...assetGroup,
        data: {
          ...assetGroup.data,
          AssetGroupSearchThemes: newSearchThemes,
        },
      },
    };
    stepService.send({ type: StepEvents.UPDATE_VALIDATION, data: dataPatch });
  }, [stepService, assetGroup, isMounted]);

  const onUpdateImageList = useCallback((imageList) => {
    if (!isMounted.current) return;
    const dataPatch = {
      assetGroup: {
        ...assetGroup,
        data: {
          ...assetGroup.data,
          Images: {
            ...assetGroup.data.Images,
            ImageList: imageList,
            ImageAssets: null,
          },
        },
      },
    };
    stepService.send({ type: StepEvents.UPDATE_VALIDATION, data: dataPatch });
  }, [stepService, assetGroup, isMounted]);

  const onClearAssets = useCallback(() => {
    if (!isMounted.current) return;
    const dataPatch = {
      assetGroup: {
        ...assetGroup,
        data: {
          ...assetGroup.data,
          Images: {
            ...assetGroup.data.Images,
            ImageList: [],
            ImageAssets: null,
          },
          Headlines: {
            Headline0: '',
            Headline1: '',
            Headline2: '',
          },
          LongHeadlines: {
            LongHeadline0: '',
          },
          Descriptions: {
            Description0: '',
            Description1: '',
          },
        },
      },
    };
    stepService.send({ type: StepEvents.UPDATE_VALIDATION, data: dataPatch });
  }, [stepService, assetGroup, isMounted]);

  // eslint-disable-next-line no-bitwise
  const [, hasInvalidSaveAttemptChanged] = usePrevProp(invalidSaveAttempt | 0);
  const [, hasServerErrorsChanged] = usePrevProp(serverErrors, serverErrorsCompare);
  const areBeaconsActive = useImpulse(hasInvalidSaveAttemptChanged || hasServerErrorsChanged);

  const [showAllValidationErrors, setShowAllValidationErrors] = useState(false);
  if (hasInvalidSaveAttemptChanged) {
    setShowAllValidationErrors(true);
  }

  const state = {
    isMounted,
    scenarioName,
    i18n,
    legacyI18n,
    locale,
    hideStepHeader,
    assetGroup,
    navigationConfirmationPrompt,
    campaignId,
    campaignName,
    campaignTimeZone,
    campaignObjective,
    campaignSettings,
    finalUrls,
    finalUrlForRecommendation,
    headlines,
    longHeadlines,
    descriptions,
    suggestedObjectiveExtensions,
    otherObjectiveExtensions,
    adExtensionsDynamicFeatures,
    adExtensionsAssociationsFactory,
    extensionsAssociations,
    urlPath1,
    urlPath2,
    finalMobileUrls,
    urlRules,
    urlRulesOption,
    images,
    businessName,
    audienceGroup,
    originalAudienceGroup,
    listingGroup,
    productFilter,
    searchThemes,
    aid,
    cid,
    permissions,
    showAllValidationErrors,
    areBeaconsActive,
    serverErrors,
    systemErrors,
    isEditing,
    isSaving,
    isCanceling,
  };
  const api = {
    onAssetGroupValidation,
    onUpdateExtensionsAssociations,
    onUpdateFinalUrls,
    onUpdateImageList,
    onClearAssets,
    onUpdateSearchThemes,
  };

  return [state, api];
};
